/**
 * 客户分类管理弹窗组件 — 提供分类的增删改查界面。
 *
 * 主要导出：`CrmCategoryModal`（自定义元素 `<crm-category-modal>`）
 *
 * 功能特性：
 * - 添加新分类（输入名称 + 回车或点击按钮）
 * - 编辑已有分类名称
 * - 删除分类（删除前弹出确认对话框，删除后关联客户自动重置为未分类）
 * - 点击遮罩层外部关闭弹窗
 * - 网络错误时静默处理，非网络错误显示 toast 提示
 *
 * 依赖关系：
 * - 继承 `BaseElement`
 * - 通过 `categoryApi` 调用分类 CRUD 接口
 * - 使用 `showConfirm` 显示删除确认对话框
 * - 使用 `isNetworkError` 判断错误类型
 *
 * 对外事件：
 * - `category-changed` — 分类数据变化后冒泡，通知父组件刷新列表
 */
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { categoryApi } from '../api.js';
import { toast } from './toast.js';
import { showConfirm } from './confirm-dialog.js';
import { isNetworkError } from '../utils/index.js';

@customElement('crm-category-modal')
export class CrmCategoryModal extends BaseElement {
  /** 分类列表数据（由父组件传入） */
  @property({ type: Array }) categories: Array<{ id: number; name: string }> = [];
  /** 弹窗是否打开 */
  @property({ type: Boolean }) open = false;

  /** 当前编辑中的分类（null 表示新增模式） */
  @state() private editingCategory: { id: number; name: string } | null = null;
  /** 表单中输入的分类名称 */
  @state() private formName = '';
  /** 标记 mousedown 是否发生在遮罩层（用于区分点击遮罩关闭 vs 拖拽选中文本） */
  private _backdropMousedown = false;

  /**
   * 关闭弹窗并重置表单状态，派发 `category-changed` 事件。
   */
  close() {
    this.open = false;
    this.editingCategory = null;
    this.formName = '';
    this.dispatchEvent(new CustomEvent('category-changed', { bubbles: true, composed: true }));
  }

  /**
   * 添加新分类 — 调用 API 创建，成功后清空表单并通知父组件。
   * 网络错误静默处理（可能是离线），其他错误显示 toast。
   * @sideeffect 调用后端创建接口，派发 `category-changed` 事件
   */
  private async addCategory() {
    const name = this.formName.trim();
    if (!name) return;
    try {
      await categoryApi.create(name);
      this.formName = '';
      this.dispatchEvent(new CustomEvent('category-changed', { bubbles: true, composed: true }));
      toast.success('分类添加成功');
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '添加失败');
    }
  }

  /**
   * 更新分类名称 — 调用 API 更新，成功后退出编辑模式并通知父组件。
   * @sideeffect 调用后端更新接口，派发 `category-changed` 事件
   */
  private async updateCategory() {
    if (!this.editingCategory) return;
    const name = this.formName.trim();
    if (!name) return;
    try {
      await categoryApi.update(this.editingCategory.id, name);
      this.editingCategory = null;
      this.formName = '';
      this.dispatchEvent(new CustomEvent('category-changed', { bubbles: true, composed: true }));
      toast.success('分类更新成功');
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '更新失败');
    }
  }

  /**
   * 删除分类 — 先弹出确认对话框，确认后调用 API 删除。
   * 删除后关联该分类的客户将自动重置为「未分类」。
   * @param id - 要删除的分类 ID
   * @sideeffect 调用后端删除接口，派发 `category-changed` 事件
   */
  private async deleteCategory(id: number) {
    if (!(await showConfirm('删除分类后，关联客户的分类将重置为"未分类"，确定删除吗？'))) return;
    try {
      await categoryApi.delete(id);
      // 如果正在编辑被删除的分类，清除编辑状态
      if (this.editingCategory?.id === id) { this.editingCategory = null; this.formName = ''; }
      this.dispatchEvent(new CustomEvent('category-changed', { bubbles: true, composed: true }));
      toast.success('分类已删除');
    } catch (err: any) {
      if (!isNetworkError(err.message)) toast.error(err.message || '删除失败');
    }
  }

  /**
   * 渲染分类管理弹窗。
   * open 为 false 时返回空模板。
   * 布局：遮罩层 → 弹窗卡片 → 标题栏（标题 + 关闭按钮）→ 表单区（输入 + 添加/更新/取消按钮）→ 分类列表。
   * @returns Lit 模板
   */
  render() {
    if (!this.open) return html``;
    return html`
      <div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50" @mousedown=${(e: Event) => { this._backdropMousedown = e.target === e.currentTarget; }} @click=${() => { if (this._backdropMousedown) this.close(); }}>
        <div class="bg-surface rounded-md shadow-xl w-full max-w-md border border-border mx-4 overflow-y-auto" style="max-height:90vh;" @click=${(e: Event) => e.stopPropagation()}>
          <div class="sticky top-0 bg-surface flex justify-between items-center px-6 pt-5 pb-4 border-b border-border">
            <h2 class="m-0 font-semibold text-xl text-text">管理客户分类</h2>
            <button class="p-1.5 rounded hover:bg-bg cursor-pointer text-text-muted transition-colors" @click=${this.close}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="p-6 space-y-3">
            <div class="flex gap-2">
              <input type="text" .value=${this.formName} @input=${(e: Event) => { this.formName = (e.target as HTMLInputElement).value; }}
                placeholder="输入分类名称"
                class="flex-1 px-3 py-2 border border-border rounded-md text-sm bg-surface text-text focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none transition-all"
                @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); if (this.editingCategory) this.updateCategory(); else this.addCategory(); } }}
              />
              ${this.editingCategory ? html`
                <button class="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer" @click=${this.updateCategory}>更新</button>
                <button class="px-3 py-1.5 text-sm font-medium text-text-secondary border border-border rounded hover:bg-bg transition-colors cursor-pointer" @click=${() => { this.editingCategory = null; this.formName = ''; }}>取消</button>
              ` : html`
                <button class="px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer" @click=${this.addCategory}>添加</button>
              `}
            </div>
            <div class="space-y-1.5 max-h-64 overflow-y-auto">
              ${this.categories.length === 0 ? html`<p class="text-sm text-text-muted text-center py-4">暂无分类</p>` : ''}
              ${this.categories.map(c => html`
                <div class="flex items-center justify-between px-3 py-2 rounded-md hover:bg-bg/50 transition-colors">
                  <span class="text-sm text-text">${c.name}</span>
                  <div class="flex gap-1">
                    <button class="px-2 py-1.5 text-xs text-text-secondary hover:text-text transition-colors cursor-pointer rounded" @click=${() => { this.editingCategory = { id: c.id, name: c.name }; this.formName = c.name; }}>编辑</button>
                    <button class="text-error hover:text-error-hover text-xs font-medium px-2.5 py-1.5 rounded transition-colors cursor-pointer" @click=${() => this.deleteCategory(c.id)}>删除</button>
                  </div>
                </div>
              `)}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
